import { httpClient } from '../utils/httpClient';
import { apiUrl } from './api';

export const dataProvider = {
  getList: (resource: string, params: any) => {
    if (resource === 'content_analytics' || resource === 'user_analytics') {
      return Promise.resolve({ data: [], total: 0 });
    }
    
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    const query = {
      page,
      limit: perPage,
      sort: `${field}_${order}`,
      ...params.filter,
    };
    
    const url = `${apiUrl}/admin/${resource}?${new URLSearchParams(query).toString()}`;
    return httpClient(url)
      .then(({ json }) => {
        if (!json.success) {
          throw new Error(json.message || 'Error loading data');
        }
        
        let data = [];
        if (resource === 'content') {
          data = json.data.content || [];
        } else if (resource === 'content_types') {
          data = json.data.content_types || [];
        } else if (resource === 'quotes') {
          data = json.data.quotes || json.data[resource] || json.data || [];
        } else {
          data = json.data[resource] || json.data || [];
        }
        
        return {
          data: data,
          total: json.data.pagination?.total || data.length,
        };
      })
      .catch(error => {
        console.error(`Error loading ${resource}:`, error instanceof Error ? error.message : 'Unknown error');
        // Return empty array but don't hide error completely
        if (error.message.includes('429') || error.message.includes('Too Many Requests')) {
          throw new Error('Too many requests. Please wait a moment and try again.');
        }
        // For ReferenceInput, return empty data to prevent blocking UI
        if (resource === 'content_types' || resource === 'categories') {
          return { data: [], total: 0 };
        }
        return { data: [], total: 0 };
      });
  },

  getOne: (resource: string, params: any) => {
    const url = `${apiUrl}/admin/${resource}/${params.id}`;
    return httpClient(url).then(({ json }) => {
      if (!json.success) {
        throw new Error(json.message || `Error loading ${resource}`);
      }
      
      let data = json.data[resource] || json.data;
      if (resource === 'content_types') {
        data = json.data.content_type || json.data;
      } else if (resource === 'categories') {
        data = json.data.category || json.data;
      } else if (resource === 'content') {
        data = json.data.content || json.data;
      } else if (resource === 'quotes') {
        data = json.data;
      }
      return { data };
    }).catch(error => {
      console.error(`Error loading ${resource} with ID ${params.id}:`, error instanceof Error ? error.message : 'Unknown error');
      throw error;
    });
  },

  getMany: (resource: string, params: any) => {
    const query = `filter=${JSON.stringify({ id: params.ids })}`;
    const url = `${apiUrl}/admin/${resource}?${query}`;
    return httpClient(url).then(({ json }) => {
      if (!json.success) {
        throw new Error(json.message || `Error loading ${resource}`);
      }
      
      let data = json.data[resource] || json.data;
      if (resource === 'content_types') {
        data = json.data.content_types || json.data;
      } else if (resource === 'categories') {
        data = json.data.categories || json.data;
      } else if (resource === 'content') {
        data = json.data.content || json.data;
      } else if (resource === 'quotes') {
        data = json.data.quotes || json.data[resource] || json.data;
      }
      return { data };
    }).catch(error => {
      console.error(`Error loading multiple ${resource}:`, error instanceof Error ? error.message : 'Unknown error');
      // Return empty data instead of throwing error to prevent blocking UI
      // This is especially important for ReferenceInput fields
      return { data: [] };
    });
  },

  getManyReference: (resource: string, params: any) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    const query = {
      page,
      limit: perPage,
      sort: `${field}_${order}`,
      [params.target]: params.id,
      ...params.filter,
    };
    const url = `${apiUrl}/admin/${resource}?${new URLSearchParams(query).toString()}`;
    return httpClient(url).then(({ json }) => {
      if (!json.success) {
        throw new Error(json.message || `Error loading related ${resource}`);
      }
      
      let data = json.data[resource] || json.data;
      if (resource === 'content_types') {
        data = json.data.content_types || json.data;
      } else if (resource === 'categories') {
        data = json.data.categories || json.data;
      } else if (resource === 'content') {
        data = json.data.content || json.data;
      } else if (resource === 'quotes') {
        data = json.data.quotes || json.data[resource] || json.data;
      }
      
      return {
        data: data,
        total: json.data.pagination?.total || data.length,
      };
    }).catch(error => {
      console.error(`Error loading related ${resource}:`, error instanceof Error ? error.message : 'Unknown error');
      // Return empty data instead of throwing error to prevent blocking UI
      // This is especially important for ReferenceInput fields
      return { data: [], total: 0 };
    });
  },

  create: async (resource: string, params: any) => {
    const url = `${apiUrl}/admin/${resource}`;
    
    try {
      if (resource === 'content' && params.data.audio_file) {
        // Create content without audio file first
        const contentData = { ...params.data };
        delete contentData.audio_file;
        
        const createResponse = await httpClient(url, {
          method: 'POST',
          body: JSON.stringify(contentData),
        });
        
        
        // Check response success
        if (!createResponse.json.success) {
          throw new Error(createResponse.json.message || 'Error creating content');
        }
        
        // Extract ID from response
        let contentId = null;
        if (createResponse.json.data) {
          // For content, API returns data.content.id
          contentId = createResponse.json.data.content?.id;
          
        }
        
        if (!contentId) {
          console.error('Failed to get ID of created content. Response structure is unexpected.');
          throw new Error('Failed to get ID of created content');
        }
        
        // If there's an audio file, upload it to S3
        if (params.data.audio_file && params.data.audio_file.rawFile) {
          const file = params.data.audio_file.rawFile;
          
          // Get presigned URL
          const presignedResponse = await httpClient(`${apiUrl}/admin/content/presigned-url`, {
            method: 'POST',
            body: JSON.stringify({
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size
            })
          });
          
          if (presignedResponse.json.success) {
            const { uploadUrl, s3Key } = presignedResponse.json.data;
            
            // Upload file to S3
            await fetch(uploadUrl, {
              method: 'PUT',
              body: file,
              headers: {
                'Content-Type': file.type
              }
            });
            
            // Confirm upload
            await httpClient(`${apiUrl}/admin/content/${contentId}/confirm-upload`, {
              method: 'POST',
              body: JSON.stringify({
                s3Key: s3Key,
                fileSize: file.size,
                contentType: file.type
              })
            });
          }
        }


        if (params.data.cover_image && params.data.cover_image.rawFile) {
          const coverFile = params.data.cover_image.rawFile;

          const coverPresignedResponse = await httpClient(`${apiUrl}/admin/content/presigned-url`, {
            method: 'POST',
            body: JSON.stringify({
              fileName: `cover_${contentId}_${coverFile.name}`,
              fileType: coverFile.type,
              fileSize: coverFile.size
            })
          });

          if (coverPresignedResponse.json.success) {
            const { uploadUrl: coverUploadUrl, s3Key: coverS3Key } = coverPresignedResponse.json.data;

            // Upload cover image to S3
            await fetch(coverUploadUrl, {
              method: 'PUT',
              body: coverFile,
              headers: {
                'Content-Type': coverFile.type
              }
            });

            // Update content with cover image info
            await httpClient(`${apiUrl}/admin/content/${contentId}`, {
              method: 'PUT',
              body: JSON.stringify({
                cover_image_key: coverS3Key,
                cover_image_url: coverUploadUrl.split('?')[0] // Remove query params
              })
            });
          }
        }
        
        return {
          data: { ...params.data, id: contentId },
        };
      }
      
      // Regular creation for other resources
      const response = await httpClient(url, {
        method: 'POST',
        body: JSON.stringify(params.data),
      });
      
      
      // Check response success
      if (!response.json.success) {
        throw new Error(response.json.message || `Error creating ${resource}`);
      }
      

      let newId = null;
      if (response.json.data) {

        if (resource === 'categories') {
          newId = response.json.data.category?.id;
        } else if (resource === 'content_types') {
          newId = response.json.data.content_type?.id;
        } else if (resource === 'content') {
          newId = response.json.data.content?.id;
        } else if (resource === 'quotes') {
          newId = response.json.data.id;
        } else {
          newId = response.json.data.id || 
                  response.json.data[resource]?.id ||
                  response.json.data[`${resource.slice(0, -1)}`]?.id;
        }
        
      }
      
        if (!newId) {
          throw new Error(`Failed to get ID of created ${resource}`);
        }
      
      const result = {
        data: { ...params.data, id: newId },
      };
      
      return result;
      
    } catch (error: any) {
      console.error(`Error creating ${resource}:`, error instanceof Error ? error.message : 'Unknown error');
      throw error;
    }
  },

  update: async (resource: string, params: any) => {
    const url = `${apiUrl}/admin/${resource}/${params.id}`;

    try {
      if (resource === 'content') {

        const updateData = { ...params.data };

        if (updateData.audio_file === null || updateData.audio_file === undefined) {
          delete updateData.audio_file;
        }

        if (updateData.audio_file && updateData.audio_file.isExisting === true) {
          delete updateData.audio_file;
        }

        if (updateData.audio_file && updateData.audio_file.rawFile) {
          const file = updateData.audio_file.rawFile;


          const presignedResponse = await httpClient(`${apiUrl}/admin/content/presigned-url`, {
            method: 'POST',
            body: JSON.stringify({
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size
            })
          });

          if (presignedResponse.json.success) {
            const { uploadUrl, s3Key } = presignedResponse.json.data;

            // Upload audio file to S3
            await fetch(uploadUrl, {
              method: 'PUT',
              body: file,
              headers: {
                'Content-Type': file.type
              }
            });

            updateData.s3_key = s3Key;
            updateData.file_size = file.size;
            updateData.content_type = file.type;
          }

          delete updateData.audio_file;
        }

        if (updateData.cover_image === null) {
          updateData.cover_image_key = null;
          updateData.cover_image_url = null;

          delete updateData.cover_image;
        } else if (updateData.cover_image && updateData.cover_image.rawFile) {
          const coverFile = updateData.cover_image.rawFile;

          const coverPresignedResponse = await httpClient(`${apiUrl}/admin/content/presigned-url`, {
            method: 'POST',
            body: JSON.stringify({
              fileName: `cover_${params.id}_${coverFile.name}`,
              fileType: coverFile.type,
              fileSize: coverFile.size
            })
          });
          if (coverPresignedResponse.json.success) {
            const { uploadUrl: coverUploadUrl, s3Key: coverS3Key } = coverPresignedResponse.json.data;

            await fetch(coverUploadUrl, {
              method: 'PUT',
              body: coverFile,
              headers: {
                'Content-Type': coverFile.type
              }
            });

            updateData.cover_image_key = coverS3Key;
            updateData.cover_image_url = coverUploadUrl.split('?')[0];
          }

          delete updateData.cover_image;
        } else if (updateData.cover_image !== null) {

          delete updateData.cover_image;
        }

        const response = await httpClient(url, {
          method: 'PUT',
          body: JSON.stringify(updateData),
        });

        if (!response.json.success) {
          throw new Error(response.json.message || `Error updating ${resource}`);
        }

        return {
          data: response.json.data[resource] || response.json.data || { id: params.id, ...params.data },
        };
      } else {
        const updateData = { ...params.data };
        if (resource === 'quotes') {
          delete updateData.id;
          delete updateData.created_at;
          delete updateData.updated_at;
        }
        
        const response = await httpClient(url, {
          method: 'PUT',
          body: JSON.stringify(updateData),
        });

        if (!response.json.success) {
          throw new Error(response.json.message || `Error updating ${resource}`);
        }

        let updatedData;
        if (resource === 'quotes') {
          updatedData = response.json.data;
        } else if (resource === 'categories') {
          updatedData = response.json.data.category || response.json.data;
        } else if (resource === 'content_types') {
          updatedData = response.json.data.content_type || response.json.data;
        } else {
          updatedData = response.json.data[resource] || response.json.data || { id: params.id, ...params.data };
        }
        
        return {
          data: updatedData,
        };
      }
    } catch (error) {
      console.error(`Error updating ${resource} with ID ${params.id}:`, error instanceof Error ? error.message : 'Unknown error');
      throw error;
    }
  },

  updateMany: (resource: string, params: any) => {
    return Promise.all(
      params.ids.map((id: string) =>
        httpClient(`${apiUrl}/admin/${resource}/${id}`, {
          method: 'PUT',
          body: JSON.stringify(params.data),
        })
      )
    ).then((responses) => {
      const results = responses.map(({ json }) => {
        if (!json.success) {
          throw new Error(json.message || `Error updating ${resource}`);
        }
        return json.data[resource] || json.data;
      });
      return { data: results };
    }).catch(error => {
      console.error(`Error bulk updating ${resource}:`, error instanceof Error ? error.message : 'Unknown error');
      throw error;
    });
  },

  delete: (resource: string, params: any) => {
    const url = `${apiUrl}/admin/${resource}/${params.id}`;
    return httpClient(url, {
      method: 'DELETE',
    }).then(({ json }) => {
      if (!json.success) {
        throw new Error(json.message || `Error deleting ${resource}`);
      }
      
      return {
        data: json.data,
      };
    }).catch(error => {
      console.error(`Error deleting ${resource} with ID ${params.id}:`, error instanceof Error ? error.message : 'Unknown error');
      throw error;
    });
  },

  deleteMany: (resource: string, params: any) => {
    return Promise.all(
      params.ids.map((id: string) =>
        httpClient(`${apiUrl}/admin/${resource}/${id}`, {
          method: 'DELETE',
        })
      )
    ).then((responses) => {
      const results = responses.map(({ json }) => {
        if (!json.success) {
          throw new Error(json.message || `Error deleting ${resource}`);
        }
        return json.data;
      });
      return { data: results };
    }).catch(error => {
      console.error(`Error bulk deleting ${resource}:`, error instanceof Error ? error.message : 'Unknown error');
      throw error;
    });
  },

  // Method for getting presigned URL for audio playback
  getAudioUrl: async (contentId: string) => {
    const url = `${apiUrl}/admin/content/${contentId}/url`;
    const response = await httpClient(url);
    return response.json.data.url;
  },
};

